/**
 * PATH: packages/ui/src/kanban/components/board-view/BoardViewOverlays.tsx
 * FILE_ID: UI-KANBAN-BOARDVIEW-OVERLAYS
 * PURPOSE: Thin wrapper that mounts <BoardOverlays> (context menus, dialogs,
 *   popovers, modals) with the full prop bundle the board chrome wires.
 * WHY: Extracted from BoardViewLayout so the layout file stays under the 500-
 *   line operator ceiling. Verbatim move of the <BoardOverlays …/> element from
 *   the KanbanBoardView monolith — zero behaviour change.
 */
import { lazy, Suspense } from 'react'

import { BoardOverlays } from './BoardOverlays'
// FEAT-2026-06-30-KANBAN-CARD-WORKBOOK-FLOWCHART — per-card editors opened from
// the card hover-menu's Calculator / Flowchart icons (board-lifted modal state).
import { KanbanCardFlowchartModal } from '../KanbanCardFlowchartModal'
// Change #755 — one left-nav settings home. The old BoardSettingsDrawer
// is a compatibility re-export and is not mounted here.
import { SettingsHome } from '../settings-home'
import { BoardActivityDialog } from './BoardActivityDialog'
import { BoardRewindDialog } from './BoardRewindDialog'
import type { BoardViewLayoutProps } from './BoardViewLayout'

const KanbanCardWorkbookModal = lazy(async () => {
  const module = await import('../KanbanCardWorkbookModal')
  return { default: module.KanbanCardWorkbookModal }
})

export function BoardViewOverlays(props: BoardViewLayoutProps) {
  const {
    selectedCardIds,
    clearCardSelection,
    hooks,
    contextMenu,
    ctxFullCardQ,
    api,
    boardId,
    board,
    queryClient,
    laneNamesRef,
    validTemplates,
    lanes,
    resolvedMembers,
    orgIdForCreate,
    filteredCards,
    checklistOpenIds,
    setChecklistOpenIds,
    setContextMenu,
    setCascadeRoot,
    setDetailCard,
    invokeMutation,
    moveMutation,
    patchCardMutation,
    deleteCardMutation,
    registerUndo,
    laneCallbacks,
    cascadeRoot,
    laneMenu,
    laneSorts,
    cardsByLane,
    onSwitchBoard,
    setLaneSorts,
    setInlinePrompt,
    setCopyLaneSrc,
    setLaneColorUI,
    setLaneMembersUI,
    setLaneMenu,
    updateLaneMutation,
    deleteLaneMutation,
    laneColorUI,
    laneMembersUI,
    laneSettingsUI,
    setLaneSettingsUI,
    boardMembersUI,
    setBoardMembersUI,
    canManageBoardMembers,
    canvasMenu,
    setCanvasMenu,
    setBoardDraftName,
    setBoardRenaming,
    updateBoardMutation,
    inlinePrompt,
    handleCreateMember,
    createCardMutation,
    createLaneMutation,
    copyLaneSrc,
    view,
    boardFind,
    wipConfirm,
    setWipConfirm,
    archivedLanesOpen,
    setArchivedLanesOpen,
    recentlyDeletedLanesOpen,
    setRecentlyDeletedLanesOpen,
    recentlyDeletedCardsOpen,
    setRecentlyDeletedCardsOpen,
    boardQ,
    deleteBoardConfirmOpen,
    setDeleteBoardConfirmOpen,
    setDeleteBoardTyped,
    deleteBoardTyped,
    cardsQ,
    cardsLoadState,
    deleteBoardMutation,
    docModal,
    setDocModal,
    boardSettingsOpen,
    setBoardSettingsOpen,
    boardSettingsPage,
    activityOpen,
    setActivityOpen,
    rewindOpen,
    setRewindOpen,
    onCardOpen,
    cardDensity,
    setCardDensity,
    keyboardMode,
    setKeyboardMode,
    setShortcutsOpen,
  } = props
  return (
    <>
      <BoardOverlays
        selectedCardIds={selectedCardIds}
        clearCardSelection={clearCardSelection}
        hooks={hooks}
        contextMenu={contextMenu}
        ctxFullCardQ={ctxFullCardQ}
        api={api}
        boardId={boardId ?? ''}
        board={board}
        queryClient={queryClient}
        laneNamesRef={laneNamesRef}
        validTemplates={validTemplates}
        lanes={lanes}
        resolvedMembers={resolvedMembers}
        orgIdForCreate={orgIdForCreate}
        filteredCards={filteredCards}
        checklistOpenIds={checklistOpenIds}
        setChecklistOpenIds={setChecklistOpenIds}
        setContextMenu={setContextMenu}
        setCascadeRoot={setCascadeRoot}
        setDetailCard={setDetailCard}
        invokeMutation={invokeMutation}
        moveMutation={moveMutation}
        patchCardMutation={patchCardMutation}
        deleteCardMutation={deleteCardMutation}
        registerUndo={registerUndo}
        setDocModal={setDocModal}
        cascadeRoot={cascadeRoot}
        laneMenu={laneMenu}
        laneSorts={laneSorts}
        cardsByLane={cardsByLane}
        onSwitchBoard={onSwitchBoard}
        setLaneSorts={setLaneSorts}
        setInlinePrompt={setInlinePrompt}
        setCopyLaneSrc={setCopyLaneSrc}
        setLaneColorUI={setLaneColorUI}
        setLaneMembersUI={setLaneMembersUI}
        setLaneMenu={setLaneMenu}
        // ARC-8 sibling-gap fix — the lane right-click "Archive this list"
        // routes through the SAME laneCallbacks.onArchive the kebab menu
        // uses, so it registers the identical Ctrl/Cmd+Z + Undo-toast entry.
        onArchiveLane={laneCallbacks.onArchive}
        updateLaneMutation={updateLaneMutation}
        deleteLaneMutation={deleteLaneMutation}
        laneColorUI={laneColorUI}
        laneMembersUI={laneMembersUI}
        laneSettingsUI={laneSettingsUI}
        setLaneSettingsUI={setLaneSettingsUI}
        boardMembersUI={boardMembersUI}
        setBoardMembersUI={setBoardMembersUI}
        canManageBoardMembers={canManageBoardMembers}
        canvasMenu={canvasMenu}
        setCanvasMenu={setCanvasMenu}
        setBoardDraftName={setBoardDraftName}
        setBoardRenaming={setBoardRenaming}
        updateBoardMutation={updateBoardMutation}
        inlinePrompt={inlinePrompt}
        handleCreateMember={handleCreateMember}
        createCardMutation={createCardMutation}
        createLaneMutation={createLaneMutation}
        copyLaneSrc={copyLaneSrc}
        view={view}
        boardFind={boardFind}
        wipConfirm={wipConfirm}
        setWipConfirm={setWipConfirm}
        archivedLanesOpen={archivedLanesOpen}
        setArchivedLanesOpen={setArchivedLanesOpen}
        recentlyDeletedLanesOpen={recentlyDeletedLanesOpen}
        setRecentlyDeletedLanesOpen={setRecentlyDeletedLanesOpen}
        recentlyDeletedCardsOpen={recentlyDeletedCardsOpen}
        setRecentlyDeletedCardsOpen={setRecentlyDeletedCardsOpen}
        boardQ={boardQ}
        deleteBoardConfirmOpen={deleteBoardConfirmOpen}
        setDeleteBoardConfirmOpen={setDeleteBoardConfirmOpen}
        setDeleteBoardTyped={setDeleteBoardTyped}
        deleteBoardTyped={deleteBoardTyped}
        cardsQ={cardsQ}
        cardsLoadState={cardsLoadState}
        deleteBoardMutation={deleteBoardMutation}
      />

      {/* FEAT-2026-06-30-KANBAN-CARD-WORKBOOK-FLOWCHART — per-card editors. Each
          re-checks its API pair (so a stale handler can never open a modal the
          host can't persist) and renders the matching editor; both close back
          to the board by clearing the single `docModal` slot. */}
      {docModal?.kind === 'workbook' && api.getCardWorkbook && api.saveCardWorkbook && (
        <Suspense fallback={null}>
          <KanbanCardWorkbookModal
            key={docModal.card.id}
            card={docModal.card}
            getWorkbook={api.getCardWorkbook}
            saveWorkbook={api.saveCardWorkbook}
            notify={hooks?.notify}
            onSaved={() => {
              // ARC-3 follow-up — a first save mints the CardDocument row;
              // refetch the board cards so the tile's sheet chip +
              // drawer's Workbook row see the new document_kinds. No-op
              // close avoids a board-wide refetch; repeated autosaves coalesce
              // inside the modal and signal this once per open session.
              void queryClient.invalidateQueries({
                queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'],
              })
            }}
            onClose={() => setDocModal(null)}
          />
        </Suspense>
      )}
      {docModal?.kind === 'flowchart' && api.getCardFlowchart && api.saveCardFlowchart && (
        <KanbanCardFlowchartModal
          card={docModal.card}
          getFlowchart={api.getCardFlowchart}
          saveFlowchart={api.saveCardFlowchart}
          notify={hooks?.notify}
          onClose={() => setDocModal(null)}
        />
      )}

      <SettingsHome
        open={boardSettingsOpen}
        onClose={() => setBoardSettingsOpen(false)}
        initialPage={boardSettingsPage}
        api={api}
        hooks={hooks}
        board={board}
        updateBoardMutation={updateBoardMutation}
        updateLaneMutation={updateLaneMutation}
        onOpenAccess={() => setBoardMembersUI({ anchor: { x: 24, y: 72 } })}
        onOpenLaneMembers={(lane) =>
          setLaneMembersUI({ lane, anchor: { x: 24, y: 72 } })
        }
        cardDensity={cardDensity}
        setCardDensity={setCardDensity}
        keyboardMode={keyboardMode}
        setKeyboardMode={setKeyboardMode}
        setShortcutsOpen={setShortcutsOpen}
        setArchivedLanesOpen={setArchivedLanesOpen}
        setRecentlyDeletedLanesOpen={setRecentlyDeletedLanesOpen}
        setRecentlyDeletedCardsOpen={setRecentlyDeletedCardsOpen}
        setDeleteBoardConfirmOpen={setDeleteBoardConfirmOpen}
        setActivityOpen={setActivityOpen}
        setRewindOpen={setRewindOpen}
        canManageBoardMembers={canManageBoardMembers}
      />

      {activityOpen && board && api.listBoardActivity && (
        <BoardActivityDialog
          api={api}
          boardId={board.id}
          onClose={() => setActivityOpen(false)}
          onOpenRewind={() => {
            setActivityOpen(false)
            setRewindOpen(true)
          }}
          queryClient={queryClient}
          hooks={hooks}
          onOpenCard={(card) => {
            setActivityOpen(false)
            onCardOpen(card)
          }}
        />
      )}

      {rewindOpen && board && api.previewBoardRestore && api.applyBoardRestore && (
        <BoardRewindDialog
          api={api}
          boardId={board.id}
          onClose={() => setRewindOpen(false)}
          onApplied={({ moves, flips, skipped }) => {
            void queryClient.invalidateQueries({
              queryKey: ['kanban', 'board', board.id],
            })
            void queryClient.invalidateQueries({
              queryKey: ['kanban', 'cards', board.id],
            })
            hooks?.notify?.(
              'success',
              `Board rewound — ${moves} move${moves === 1 ? '' : 's'}, ` +
                `${flips} archive change${flips === 1 ? '' : 's'}` +
                (skipped > 0 ? `, ${skipped} skipped` : '') +
                '.',
            )
          }}
        />
      )}
    </>
  )
}
